import { LoadingOverlay } from '@mantine/core';
import type { RefObject } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ContentPlaceholder } from '../../../../components/content-placeholder/content-placeholder';
import { useProcessAnalysisStore } from '../../../../store/process-analysis-store/process-analysis-store';
import { useSelectedMeasures } from '../../../../store/process-analysis-store/process-analysis-store.hooks';
import { useAutoSelectFilters } from '../../filters/measure-comparison/measure-comparison.hooks';
import {
  useMeasureComparisonData,
  useMeasureComparisonTargets,
  useTimeAllocationsData,
} from '../../hooks/measure-comparison';
import { MeasureComparisonLineChart } from './line-chart/line-chart';
import { isEmptySelectedData, mergeMeasureMetaDataAndTargets } from './measure-comparison.helpers';

export const MeasureComparison = ({ chartRef }: { chartRef: RefObject<HTMLDivElement | null> }) => {
  const i18n = useTranslation();
  const selectedMeasures = useSelectedMeasures();
  const availableMeasures = useProcessAnalysisStore((state) => state.availableMeasures);

  const { data, isFetching, isLoading } = useMeasureComparisonData();
  const availableTargets = useMeasureComparisonTargets();
  useTimeAllocationsData();

  useAutoSelectFilters();

  const mergedMeasureMetadata = useMemo(() => {
    return mergeMeasureMetaDataAndTargets(availableMeasures, availableTargets);
  }, [availableMeasures, availableTargets]);

  // Empty-state placeholder still gates on `isFetching` so that a transient
  // refetch with no cached value yet does not flash the empty CTA.
  if (!isFetching && (!data || Object.keys(data).length === 0)) {
    return <ContentPlaceholder />;
  }

  if (selectedMeasures.length === 0) {
    return <ContentPlaceholder noFiltersApplied />;
  }

  if (isEmptySelectedData(data, selectedMeasures)) {
    return <ContentPlaceholder text={i18n.t('common.labels.no_filtered_data')} />;
  }

  return (
    <div style={{ position: 'relative', height: '100%', width: '100%' }}>
      <LoadingOverlay visible={isLoading} overlayProps={{ blur: 2 }} style={{ zIndex: 200 }} />
      <MeasureComparisonLineChart ref={chartRef} data={data} mergedMeasureMetadata={mergedMeasureMetadata} />
    </div>
  );
};
